import { Measure } from '@cbsi-cbscom/measure';
import { IImportable } from 'library/ts/interfaces/importable/IImportable';
import { IImporterReject } from 'library/ts/interfaces/importer/IImporterReject';
import { IImporterResolve } from 'library/ts/interfaces/importer/IImporterResolve';
import { IImportVO } from 'library/ts/interfaces/importer/IImportVO';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { Metrics } from 'library/ts/utilities/measure/Metrics';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagAPI } from 'tag/ts/tag/api/TagAPI';
import { VideoAPI } from 'tag/ts/tag/api/VideoAPI';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { ITagAPI } from 'tag/ts/tag/interfaces/api/ITagAPI';
import { IVideoAPI } from 'tag/ts/tag/interfaces/api/IVideoAPI';
import { ITagControllerArgument } from 'tag/ts/tag/interfaces/arguments/ITagControllerArgument';
import { ITagController } from 'tag/ts/tag/interfaces/mvc/ITagController';
import { ITagPlayerManager } from 'tag/ts/tag/interfaces/player/ITagPlayerManager';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { TagErrorCode } from 'tag/ts/tag/model/error/TagErrorCode';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { EmbedManager } from 'tag/ts/tag/player/managers/EmbedManager';
import { PlayerManager } from 'tag/ts/tag/player/managers/PlayerManager';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Tag Controller
 */
export class TagController extends AbstractTagClass implements ITagController {

	#embedManager: EmbedManager;
	#playerManager: ITagPlayerManager;
	#tagAPI: ITagAPI;
	#videoAPI: IVideoAPI;
	#failedImports: IImportVO[];

	constructor( argument: ITagControllerArgument ) {
		super(
			{
				...argument,
				name: 'TagController'
			}
		);
		// Just create instances - initialization happens in initialize() method
		this.#embedManager = new EmbedManager( { tag: this.tag } );
		this.#playerManager = new PlayerManager( { tag: this.tag } );
	}

	/**
	 * @description Initialize TagController and all its components
	 * Coordinates sequential async initialization while preserving exact sequence
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	initialize = async (): Promise<IPromiseResolveGeneric> => {
		try {
			this?.logger?.log( 'initialize: Starting TagController initialization' );

			// Initialize PlayerManager first (creates player adapter, loads dependencies)
			await ( this.#playerManager as PlayerManager ).initialize();
			this?.logger?.log( 'initialize: PlayerManager initialized' );

			// Add small delay to preserve original timing
			await this.utils.Delay.delay();

			// Setup tag events handler
			this.tagEventsHandler(
				this.#onTagEvent,
				[
					TagEvent.ON_IMPORTS_FAILURE
				]
			);

			// Load dependencies on session start (Shaka, IMA, etc.)
			await this.loadDependencies();
			this?.logger?.log( 'initialize: Dependencies loaded' );

			// Wait for EmbedManager to signal it's ready (handles skin, imports done, etc.)
			await this.#embedManager.waitForReady?.();
			this?.logger?.log( 'initialize: EmbedManager ready' );

			// Inform tag application that controller is ready
			this.dispatchTagEvent(
				new TagEventVO(
					TagEvent.ON_CONTROLLER_READY,
					this.tag,
					undefined,
					this
				)
			);

			this?.logger?.log( 'initialize: TagController initialization complete' );
			return { msg: 'TagController initialized successfully', success: true };

		} catch ( error ) {
			this?.logger?.error( 'initialize: TagController initialization failed:', error );
			throw error;
		}
	};

	/**
	 * @description Returns PlayerManager
	 */
	get playerManager() {
		return this.#playerManager;
	}

	/**
	 * @description Returns array of failed imports
	 */
	get failedImports(): IImportVO[] {
		return this.#failedImports;
	}

	/**
	 * @description Returns boolean flag for IMA load failure
	 * @return {boolean}
	 */
	get imaLoadFailed(): boolean {
		if ( this.utils.Type.isEmpty( this.#failedImports ) ) {
			return false;
		} else {
			const importsModule = this.tag.model.config.tag.Imports.params; // Imports specified in Tag config Imports module
			// check if ima is present in failed imports array + return boolean flag
			return this.utils.Array.hasObjWithPropValue( this.failedImports, 'namespace', ( importsModule.ima as IImportable ).namespace );
		}
	}

	/**
	 * @description Returns instance of TagAPI class
	 */
	get tagAPI() {
		if ( this.utils.Type.isUndefined( this.#tagAPI ) ) this.#tagAPI = new TagAPI( { tag: this.tag } );
		return this.#tagAPI;
	}

	/**
	 * @description Returns instance of VideoAPI class
	 */
	get videoAPI() {
		if ( this.utils.Type.isUndefined( this.#videoAPI ) ) this.#videoAPI = new VideoAPI( { tag: this.tag } );
		return this.#videoAPI;
	}

	/**
	 * @description Load dependencies
	 * @param {boolean} sessionStart
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	loadDependencies = ( sessionStart: boolean = true ): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				// get imports array
				const imports: IImportVO[] = this.tag.tagUtilities.getImports( this.tag );

				this?.logger?.log( 'loadDependencies', 'imports:', imports );

				// dispatch event to signal dependencies have started loading
				if ( sessionStart === true ) {
					this.dispatchTagEvent(
						new TagEventVO(
							TagEvent.ON_START_DEPENDENCIES_LOAD,
							this.tag,
							undefined,
							this
						)
					);
				}

				if ( sessionStart === true && this?.tag?.params?.SYSTEM?.embedType === EmbedType.DESKTOP ) {
					Measure.start( Metrics.importsLoadTime );
				}

				// load dependencies
				this.utils.Importer.import( imports, true ).then(
					( data: IImporterResolve ) => {
						this?.logger?.log( 'Dependencies import success:', data );
						// inform application + start player embed process
						this.dispatchTagEvent(
							new TagEventVO(
								// decide on event type per dependencies load request on session start or post session start
								sessionStart === true ? TagEvent.ON_SESSION_START_IMPORTS_DONE : TagEvent.ON_IMPORTS_DONE,
								this.tag,
								undefined,
								this
							)
						);

						if ( sessionStart === true && this?.tag?.params?.SYSTEM?.embedType === EmbedType.DESKTOP ) {
							Measure.end( Metrics.importsLoadTime );
						}

						resolve( { success: true } );
					}
				).catch(
					( error: IImporterReject ) => {
						this?.logger?.warn( 'loadDependencies:', 'Dependencies import failure:', error );
						// inform application
						this.dispatchTagEvent(
							new TagEventVO(
								TagEvent.ON_IMPORTS_FAILURE,
								this.tag,
								error,
								this
							)
						);
						reject( { success: false } );
					}
				);
			}
		);
	};

	/**
	 * @description Process dependency load failure and show error panel if needed
	 * @param {IImporterReject} error
	 * @param {boolean} sessionStart - indicates if we are loading dependencies on Smart Tag session start, or repeating it later on in SPA mode
	 */
	#processDependencyLoadFailure = ( error: IImporterReject, sessionStart: boolean = true ): void => {
		// store failed imports
		this.#failedImports = error.imports;

		// check what imports failed + dispatched fatal error is needed

		let ima: boolean = false;
		let player: boolean = false;
		let imaImportVO: IImportVO;
		let playerImportVO: IImportVO;

		for ( let i = 0; i < error.imports.length; i++ ) {
			if ( ima === false ) {
				if (
					error.imports[ i ].namespace.includes( this.tag.model.config.tag.Imports.params.ad_sdk.ima.namespace )
					||
					error.imports[ i ].namespace.includes( this.tag.model.config.tag.Imports.params.ad_sdk.ima_dai.namespace )
				) {
					ima = true;
					imaImportVO = error.imports[ i ];
				}
			}
			if ( player === false ) {
				if ( error.imports[ i ].namespace.includes( this.tag.model.config.tag.Imports.params.avia.player.namespace ) ) {
					player = true;
					playerImportVO = error.imports[ i ];
				}
			}
		}

		this?.logger?.warn( 'Failed imports:', '\nima:', ima, imaImportVO, '\nplayer:', player, playerImportVO );

		// setup event object
		const eventVO = this.getTagEventVO(
			TagEvent.ON_FATAL_ERROR,
			this.tag,
			{
				fatalError: {
					code: undefined,
					msg: {
						original: undefined,
						custom: undefined
					}
				}
			},
			this
		);

		// VTG player load failure has precedence
		if ( player === true ) {
			// tag fatal error event dispatched internally
			eventVO.data.fatalError.code = TagErrorCode.DEPENDENCY_LOAD_FAILURE;
			eventVO.data.fatalError.msg.custom = this.tag.errorManager.createErrorMessageByCode( TagErrorCode.DEPENDENCY_LOAD_FAILURE );
			this.dispatchTagEvent( eventVO );
			return;
		}

		// IMA load failure fatal error
		if ( ima === true ) {
			// tag fatal error event dispatched internally
			eventVO.data.fatalError.code = TagErrorCode.IMA_LOAD_FAILURE;
			eventVO.data.fatalError.msg.custom = this.tag.errorManager.createErrorMessageByCode( TagErrorCode.IMA_LOAD_FAILURE );
			this.dispatchTagEvent( eventVO );
		}
	};

	/**
	 * @description Tag application event handler
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		// filter by event type
		switch ( event?.type ) {
			case TagEvent.ON_IMPORTS_FAILURE:
				this?.logger?.log( TagLoggerString.ON_TAG_EVENT, event?.type, event );
				// process initial imports failure
				this.#processDependencyLoadFailure( event?.data as IImporterReject );
				break;

		}
	};

}